import { Batcher } from './index';
import { IElevioEvents } from './events';

type ConstructorOpts = {
  interval?: number;
  ready?: boolean;
  withUnload?: boolean;
  handler: (events: Array<IElevioEvents>, isSync: boolean) => void;
};

class Batch implements Batcher {
  /**
   * The list of events that are to be sent.
   */
  queue: Array<IElevioEvents> = [];

  /**
   * Accepts an array of events and 'handles' them.
   */
  handler: (events: Array<IElevioEvents>, isSync: boolean) => void;
  private _isReady: boolean;
  private _intervalTime: number;
  _timer: undefined | number;

  /**
   * @param opts allows us to change the behavior of the batching.
   * NOTE: all options contain defaults.
   */
  constructor(opts: ConstructorOpts) {
    const { ready = true, interval = 500, handler, withUnload = true } = opts;
    this._isReady = ready;
    this._intervalTime = interval;
    this.handler = handler;

    // This is we watch for the window unload event.
    if (withUnload)
      window.addEventListener('unload', () => this.flush(true), false);
  }

  get isReady() {
    return this._isReady;
  }

  set isReady(ready) {
    this._isReady = ready;

    // If we are ready and have events in the queue then get them ready to flush
    if (ready) {
      if (this.queue.length > 0) {
        this.startTimer();
      }
    } else {
      // Stop the timer if it's running, we aren't ready!
      this.cancelTimer();
    }
  }

  startTimer() {
    // Already have a timer running.
    if (this._timer) return;

    // Don't want to flush stuff if we're not ready!
    if (!this._isReady) return;
    this._timer = window.setInterval(
      () => this.flush(false),
      this._intervalTime
    );
  }

  cancelTimer() {
    if (this._timer) window.clearInterval(this._timer);
    this._timer = undefined;
  }

  /**
   * Adds an event the the queue
   * @param event The event to add to the queue.
   */
  addEvent(event: IElevioEvents) {
    this.queue.push(event);
    this.startTimer();
  }

  /**
   * Sends all of the current queue to the 'handler' func.
   */
  flush(isSync: boolean) {
    if (this.queue.length === 0) return;
    // TODO: this line can crash. So it will be in a loop.
    this.handler(this.queue, isSync);
    this.queue = [];
    this.cancelTimer();
  }
}

export default Batch;
